/**
 * ForgotPassword.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from './ToastContainer';
import CustomCaptcha from './CustomCaptcha';
import LoadingSpinner from './LoadingSpinner';
import { getIcon } from '../utils/icons';
import './Login.css';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const { showSuccess, showError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!captchaValue) {
      setError('Please complete the human verification');
      showError('Please complete the human verification');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setEmailSent(true);
        showSuccess('Password reset email sent! Check your inbox.');
      } else {
        setError(data.error || 'Failed to send reset email');
        showError(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      showError('Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container forgot-password-container">
      {isLoading && (
        <div className="loading-spinner-fullscreen">
          <LoadingSpinner message="Sending reset link..." />
        </div>
      )}
      <div className="login-box forgot-password-box">
        <h2>{getIcon('login')} Reset Password</h2>
        <p className="login-subtitle">
          <Link to="/register">{getIcon('register')} Sign Up</Link> · <Link to="/login">{getIcon('login')} Sign In</Link>
        </p>
        
        {!emailSent ? (
          <form onSubmit={handleSubmit}>
            <p className="form-description">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {error && <p className="error-message">{error}</p>}
            
            <div className="form-group">
              <label htmlFor="email">{getIcon('mail')} Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                required
                autoFocus
                placeholder="you@example.com"
              />
            </div>

            <div className="captcha-submit-row">
              <div className="captcha-container">
                <CustomCaptcha onVerify={(verified) => setCaptchaValue(verified)} />
              </div>

              <button type="submit" disabled={isLoading || !captchaValue} className="login-button">
                {isLoading ? 'Sending...' : `${getIcon('send')} Send Reset Link`}
              </button>
            </div>
          </form>
        ) : (
          <div className="success-message-container">
            <div className="success-icon">✓</div>
            <h3>Check Your Email</h3>
            <p className="success-description">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="success-instructions">
              Click the link in the email to reset your password. The link will expire in 1 hour.
            </p>
            <p className="success-note">
              Didn't receive the email? Check your spam folder or{' '}
              <button 
                type="button" 
                onClick={() => {
                  setEmailSent(false);
                  setCaptchaValue(null);
                }}
                className="link-button"
              >
                try again
              </button>
            </p>
            <div className="form-footer">
              <p><Link to="/login">{getIcon('login')} Back to Sign In</Link></p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;

